import { GenericModelApi } from "./GenericModelApi";
import { ApiSchema, FetchResponse } from "./ApiClient";
import { DebitnoteProductApi, DebitnoteProductDataType } from "./DebitnoteProductApi";
import { DebitnoteFormData, DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import { omitProperties } from "@utils/Utils";
import { DocumentSequenceApi } from "./DocumentSequenceApi";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";
import { ProductApi } from "./ProductsApi";
import { saveEDocumentViaLambda } from "./EDocumentSaveApi";

export type DebitnoteDataType = Partial<ApiSchema["Debitnote"]["type"] & DebitnoteFormData>;

class Api extends GenericModelApi<"Debitnote", DebitnoteDataType> {
    protected userSortKeyDateField = "created" as const

    async fetchAll(filters: any = {}, token?: string, limit: number = 20): Promise<FetchResponse<DebitnoteDataType>> {
        const selectionSet = [
            ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "debitnoteProducts.*"
        ]

        return super.fetchAll(filters, token, limit, selectionSet);
    }

    /**
     * Claves de Hacienda de todas las notas del tenant, para detectar duplicados en la carga
     * masiva. Trae solo `id` y `document_key` porque son miles de registros.
     */
    async fetchAllDocumentKeys(): Promise<Set<string>> {
        const keys = new Set<string>();
        let token: string | undefined = undefined;

        do {
            const { items, nextToken }: FetchResponse<DebitnoteDataType> = await super.fetchAll(
                {},
                token,
                1000,
                ["id", "document_key"],
                { noCache: true }
            );
            items.forEach((debitnote) => {
                if (debitnote.document_key) keys.add(`${debitnote.document_key}`);
            });
            token = nextToken;
        } while (token);

        return keys;
    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<DebitnoteDataType>> {
        const selectionSet = [
            ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoice.*",
            "invoice.client.*",
            "invoice.currency.*",
            "debitnoteProducts.*"
        ]
        return await super.fetchAllByPeriod("listDebitnoteByFechaTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })
    }

    /**
     * Guardado transaccional en una sola llamada (lambda saveEDocument).
     */
    async createDebitnoteAndProducts(
        input: DebitnoteDataType,
        products: DebitnoteProductDataType[],
        reduceStock: boolean = true
    ): Promise<DebitnoteDataType> {
        const lines = products.map((p) => omitProperties({ ...p }, ["id", "forceRecalculatePrice"], true))

        const data = await saveEDocumentViaLambda<DebitnoteDataType>({
            documentType: "Debitnote",
            document: { ...input, price_note: !reduceStock } as Record<string, unknown>,
            lines,
            stockMode: reduceStock ? "reduce" : "none",
        })

        this.invalidateAllByTenant()
        this.clearCache()
        DebitnoteProductApi.invalidateAllByTenant()
        ProductApi.invalidateAllByTenant()
        DocumentSequenceApi.invalidateAllByTenant()
        DocumentAutoGeneratedNumberApi.invalidateAllByTenant()

        return data;
    }

}
const api = new Api("Debitnote", "numero", "DESC", "numero")
api.setCreateDataFieldName("created")
export const DebitnoteApi = api